import styled from 'styled-components';
import { FORECASTING_THEME } from './forecastingTheme';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

export const Page = styled.div`
  min-height: 100vh;
  background: ${FORECASTING_THEME.pageBg};
  color: ${FORECASTING_THEME.text};
  padding: 24px 28px 40px;
  box-sizing: border-box;
`;

export const HeaderLogo = styled.img`
  height: 44px;
  width: auto;
  display: block;
  object-fit: contain;
  position: relative;
  z-index: 1;
`;

export const PageHeader = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 44px;
  margin-bottom: 24px;
`;

export const HeaderTitle = styled.h1`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${FORECASTING_THEME.text};
  white-space: nowrap;
  pointer-events: none;
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: 196px 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const Sidebar = styled.aside`
  background: ${FORECASTING_THEME.panelBg};
  border: 1px solid ${FORECASTING_THEME.panelBorder};
  border-radius: 8px;
  padding: 12px;
  position: sticky;
  top: 16px;
  z-index: 1;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.15);
    border-radius: 2px;
  }

  &.forecasting-filters {
    .ant-select-single.ant-select-sm {
      height: 26px;
    }

    .ant-select-selector {
      background: ${FORECASTING_THEME.filterInputBg} !important;
      border-color: ${FORECASTING_THEME.filterBorder} !important;
      color: ${FORECASTING_THEME.text} !important;
      min-height: 26px !important;
      font-size: 12px !important;
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      color: ${FORECASTING_THEME.text} !important;
      line-height: 24px !important;
    }

    .ant-select-clear,
    .ant-select-arrow {
      color: ${FORECASTING_THEME.textMuted};
    }

    .ant-select:hover .ant-select-selector,
    .ant-select-focused .ant-select-selector {
      border-color: rgba(${APP_SHELL_THEME.overlayRgb}, 0.35) !important;
    }

    .ant-picker {
      background: ${FORECASTING_THEME.filterInputBg} !important;
      border-color: ${FORECASTING_THEME.filterBorder} !important;
      padding: 2px 8px 2px;
    }

    .ant-picker-input > input {
      color: ${FORECASTING_THEME.text} !important;
      font-size: 12px !important;
    }

    .ant-picker-input > input::placeholder {
      color: ${FORECASTING_THEME.textMuted} !important;
    }

    .ant-picker-suffix,
    .ant-picker-separator {
      color: ${FORECASTING_THEME.textMuted};
    }

    .ant-picker:hover,
    .ant-picker-focused {
      border-color: rgba(${APP_SHELL_THEME.overlayRgb}, 0.35) !important;
    }
  }
`;

export const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

export const ThreeMonthMain = styled.main`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  overflow-x: auto;
`;

export const Panel = styled.section`
  background: ${FORECASTING_THEME.panelBg};
  border: 1px solid ${FORECASTING_THEME.panelBorder};
  border-radius: 8px;
  padding: 16px 20px;
`;

export const FilterDateHint = styled.div`
  font-size: 9px;
  color: ${FORECASTING_THEME.textMuted};
  margin-top: 3px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FilterLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: ${FORECASTING_THEME.textMuted};
  margin-bottom: 3px;
  letter-spacing: 0.02em;
`;

export const FilterLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 8px;
  margin-bottom: 3px;

  &:first-child {
    margin-top: 0;
  }

  ${FilterLabel} {
    margin: 0;
  }
`;

export const FilterClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.1);
  color: ${FORECASTING_THEME.textMuted};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 9px;
  line-height: 1;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.18);
    color: ${FORECASTING_THEME.text};
  }
`;

/** "Select all" above a filter's options. */
export const FilterPopupActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 2px 8px 6px;
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(${APP_SHELL_THEME.overlayRgb}, 0.1);

  button {
    padding: 0;
    border: none;
    background: none;
    color: ${FORECASTING_THEME.accent};
    font-size: 12px;
    cursor: pointer;
  }
`;

/** A filter option with its "Only" shortcut, shown while the row is hovered. */
export const FilterOptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .filter-only {
    visibility: hidden;
    flex-shrink: 0;
    padding: 0 2px;
    border: none;
    background: none;
    color: ${FORECASTING_THEME.accent};
    font-size: 11px;
    cursor: pointer;
  }

  .ant-select-item-option:hover & .filter-only,
  .ant-select-item-option-active & .filter-only {
    visibility: visible;
  }
`;

export const ClearAllFiltersButton = styled.button`
  width: 100%;
  margin-bottom: 10px;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid rgba(${APP_SHELL_THEME.overlayRgb}, 0.2);
  background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.06);
  color: ${FORECASTING_THEME.textMuted};
  font-size: 11px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.1);
    color: ${FORECASTING_THEME.text};
    border-color: rgba(${APP_SHELL_THEME.overlayRgb}, 0.3);
  }
`;

export const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* Four groups on the quarterly page: Booked & Pipe joins the other three. */
export const QuarterKpiRow = styled(KpiRow)`
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const KpiGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const KpiGroupTitle = styled.div`
  grid-column: 1 / -1;
  font-size: 12px;
  font-weight: 600;
  color: ${FORECASTING_THEME.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: -4px;
`;

export const KpiCard = styled.button<{ $border: string; $active?: boolean }>`
  background: ${APP_SHELL_THEME.recessBg};
  border: 2px solid ${({ $border }) => $border};
  box-shadow: ${APP_SHELL_THEME.cardShadow};
  border-radius: 6px;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
  color: ${FORECASTING_THEME.text};

  ${({ $active }) =>
    $active &&
    `
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.08);
    box-shadow: 0 0 0 1px rgba(${APP_SHELL_THEME.overlayRgb}, 0.15);
  `}

  &:hover {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.06);
  }

  .label {
    font-size: 11px;
    color: ${FORECASTING_THEME.textMuted};
    margin-bottom: 6px;
  }

  .value {
    font-size: 20px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }
`;

export const ChartRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

/** Annual view: the monthly chart takes the width, the channel list a narrow
 *  column -- narrowed further so the chart, not the mix, is what reads first. */
export const WideChartRow = styled(ChartRow)`
  grid-template-columns: minmax(0, 1fr) minmax(170px, 200px);
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const ChartTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${FORECASTING_THEME.textMuted};
`;

/** Suppress Recharts focus ring (blue outline) when clicking bars. */
export const ChartPlot = styled.div`
  .recharts-wrapper,
  .recharts-surface,
  .recharts-wrapper svg,
  .recharts-surface:focus,
  .recharts-wrapper:focus,
  .recharts-surface:focus-visible,
  .recharts-wrapper:focus-visible {
    outline: none !important;
  }
`;

export const DrillToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;

  .drill-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .drill-hint {
    font-size: 11px;
    color: ${FORECASTING_THEME.textMuted};
  }

  .drill-breadcrumb {
    font-size: 11px;
    color: ${FORECASTING_THEME.textMuted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 480px;
  }

  .drill-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    border: 1px solid rgba(${APP_SHELL_THEME.overlayRgb}, 0.2);
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.06);
    color: ${FORECASTING_THEME.accentOrange};
    cursor: pointer;
    transition: background 0.15s;

    &:hover:not(:disabled) {
      background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.12);
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }
`;

export const Legend = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 400;
  color: ${FORECASTING_THEME.dataText};

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  i {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    display: inline-block;
  }
`;

/** A legend entry that filters the chart to its series when clicked. */
export const LegendButton = styled.button<{ $dimmed?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 4px;
  border: none;
  border-radius: 3px;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
  transition: opacity 0.15s, background 0.15s;

  &:hover {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.06);
  }

  &:focus-visible {
    outline: 1px solid rgba(${APP_SHELL_THEME.overlayRgb}, 0.4);
  }
`;

/** A KpiCard that only shows a figure: nothing to click. */
export const KpiStat = styled(KpiCard).attrs({ as: 'div' })`
  cursor: default;

  &:hover {
    background: ${APP_SHELL_THEME.recessBg};
  }
`;

/* One slim line in place of the logo and centred title, so the first screen
   reaches the tables. The menu already carries the logo. */
export const CompactHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const CompactTitle = styled.h1`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${FORECASTING_THEME.text};
  white-space: nowrap;
`;

export const CompactSubtitle = styled.span`
  font-size: 12px;
  color: ${FORECASTING_THEME.textMuted};

  strong {
    color: ${FORECASTING_THEME.text};
  }
`;

export const FiltersToggle = styled.button<{ $open: boolean }>`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${FORECASTING_THEME.text};
  background: ${({ $open }) => ($open ? `rgba(${APP_SHELL_THEME.overlayRgb}, 0.08)` : APP_SHELL_THEME.recessBg)};
  border: 1px solid ${FORECASTING_THEME.panelBorder};
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.06);
  }
`;

/* The filter column only takes width while it is open. */
export const CollapsibleLayout = styled(Layout)<{ $filtersOpen: boolean }>`
  grid-template-columns: ${({ $filtersOpen }) => ($filtersOpen ? '196px 1fr' : '1fr')};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const ActiveLensHint = styled.div`
  font-size: 12px;
  color: ${FORECASTING_THEME.textMuted};
  margin-bottom: 16px;
  padding: 8px 12px;
  background: rgba(${APP_SHELL_THEME.overlayRgb}, 0.05);
  border-radius: 4px;

  strong {
    color: ${FORECASTING_THEME.text};
  }
`;

export const ThreeMonthLayout = styled.div`
  display: grid;
  grid-template-columns: 248px minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 8px;
`;

export const ThreeMonthTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: stretch;

  > * {
    flex: 1 1 420px;
    min-width: min(100%, 420px);
  }
`;

export const ThreeMonthBottomRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: stretch;

  > * {
    flex: 1 1 380px;
    min-width: min(100%, 380px);
  }
`;

export const ChartPanel = styled(Panel)`
  min-width: 0;
  overflow: hidden;
`;

export const TablePanel = styled(Panel)`
  min-width: 0;
  overflow: hidden;
`;
